import React, { useCallback, useEffect, useState, useRef } from 'react';
/**
 * ScratchpadCanvas — Klad-oppervlak met TWEE modi: tekenen of typen.
 *
 * Achtergrond: sommige leerlingen rekenen liever uit door iets op te
 * schrijven (vooral op een laptop zonder pen/touch). Tekenen is fijn op
 * tablet/iPad, typen is fijn op desktop. We laten de leerling kiezen, en
 * onthouden de voorkeur in localStorage zodat ze niet steeds opnieuw
 * hoeven schakelen.
 *
 * Mouse + touch + stylus support via Pointer Events. Strokes are stored as
 * an array of paths so we can support Undo + Clear. Canvas auto-resizes to
 * its container with a devicePixelRatio backing buffer so lines stay crisp
 * on retina/mobile.
 *
 * Design intent — see APP_FEEL_2027_UPGRADE §16 (D pending → scratchpad).
 * Treated as pure tooling: nothing here is read by mastery, governance, or
 * telemetry. It is a real piece of scratch paper, not a logged behaviour.
 */
import { Eraser, Undo2, PenLine, Type } from 'lucide-react';
type Point = {
  x: number;
  y: number;
};
type Stroke = Point[];
type ScratchpadMode = 'draw' | 'type';
interface ScratchpadCanvasProps {
  /**
   * Optional reset signal — when this value changes, the canvas/textarea
   * clear. Used by PracticeSession to wipe the scratchpad between questions.
   */
  resetKey?: string | number;
}
const MODE_STORAGE_KEY = 'meester-djay.scratchpad-mode';
function readStoredMode(): ScratchpadMode {
  if (typeof window === 'undefined') return 'draw';
  try {
    const v = window.localStorage.getItem(MODE_STORAGE_KEY);
    return v === 'type' ? 'type' : 'draw';
  } catch {
    return 'draw';
  }
}
function persistMode(mode: ScratchpadMode) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore quota / privacy errors — preference is non-essential */
  }
}
export function ScratchpadCanvas({
  resetKey
}: ScratchpadCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const currentStrokeRef = useRef<Stroke | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const [mode, setMode] = useState<ScratchpadMode>(() => readStoredMode());
  const [typedText, setTypedText] = useState('');
  // ── Draw helpers ─────────────────────────────────────────
  const redrawAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.4 * dpr;
    ctx.strokeStyle = '#1e293b'; // slate-800, ink colour
    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x * dpr, stroke[0].y * dpr);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i].x * dpr, stroke[i].y * dpr);
      }
      ctx.stroke();
    }
  }, []);
  // ── Sizing ───────────────────────────────────────────────
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    redrawAll();
  }, [redrawAll]);
  useEffect(() => {
    if (mode !== 'draw') return;
    resizeCanvas();
    const ro = new ResizeObserver(resizeCanvas);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [resizeCanvas, mode]);
  // ── External reset (per question) ────────────────────────
  // Wipes BOTH modes so a fresh question always starts on a blank sheet,
  // regardless of which mode the learner happens to be in.
  useEffect(() => {
    strokesRef.current = [];
    currentStrokeRef.current = null;
    setHasInk(false);
    setTypedText('');
    redrawAll();
  }, [resetKey, redrawAll]);
  // Persist mode preference whenever it changes.
  useEffect(() => {
    persistMode(mode);
  }, [mode]);
  // When switching INTO type-mode, give the textarea immediate focus so the
  // learner can start typing without an extra tap.
  useEffect(() => {
    if (mode === 'type') {
      // Small delay to let the textarea mount.
      const t = setTimeout(() => textareaRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
  }, [mode]);
  // ── Pointer handlers ─────────────────────────────────────
  const getLocalPoint = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    const p = getLocalPoint(e);
    currentStrokeRef.current = [p];
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!currentStrokeRef.current) return;
    const p = getLocalPoint(e);
    currentStrokeRef.current.push(p);
    // Incremental draw for snappy feedback
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const stroke = currentStrokeRef.current;
    if (stroke.length < 2) return;
    const prev = stroke[stroke.length - 2];
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.4 * dpr;
    ctx.strokeStyle = '#1e293b';
    ctx.beginPath();
    ctx.moveTo(prev.x * dpr, prev.y * dpr);
    ctx.lineTo(p.x * dpr, p.y * dpr);
    ctx.stroke();
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas?.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
    if (currentStrokeRef.current && currentStrokeRef.current.length > 1) {
      strokesRef.current.push(currentStrokeRef.current);
      setHasInk(true);
    }
    currentStrokeRef.current = null;
  };
  const handleUndo = () => {
    strokesRef.current.pop();
    setHasInk(strokesRef.current.length > 0);
    redrawAll();
  };
  const handleClear = () => {
    if (mode === 'draw') {
      strokesRef.current = [];
      currentStrokeRef.current = null;
      setHasInk(false);
      redrawAll();
    } else {
      setTypedText('');
      textareaRef.current?.focus();
    }
  };
  const canClear = mode === 'draw' ? hasInk : typedText.length > 0;
  return <div className="flex flex-col h-full w-full" data-id="element-10994">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-amber-200/70 bg-amber-50/60" data-id="element-10995">
        {/* Mode toggle — segmented control */}
        <div role="tablist" aria-label="Klad-modus" className="inline-flex items-center rounded-full bg-white/80 border border-amber-200 p-0.5 shadow-sm" data-id="element-10996">
          <button type="button" role="tab" aria-selected={mode === 'draw'} onClick={() => setMode('draw')} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${mode === 'draw' ? 'bg-amber-500 text-white shadow-sm' : 'text-amber-800 hover:bg-amber-100'}`} data-id="element-10997">
            <PenLine className="w-3.5 h-3.5" data-id="element-10998" />
            Tekenen
          </button>
          <button type="button" role="tab" aria-selected={mode === 'type'} onClick={() => setMode('type')} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${mode === 'type' ? 'bg-amber-500 text-white shadow-sm' : 'text-amber-800 hover:bg-amber-100'}`} data-id="element-10999">
            <Type className="w-3.5 h-3.5" data-id="element-11000" />
            Typen
          </button>
        </div>
        <div className="flex items-center gap-2" data-id="element-11001">
          {mode === 'draw' && <button onClick={handleUndo} disabled={!hasInk} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-slate-700 bg-white/80 border border-slate-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Laatste streep ongedaan maken" data-id="element-11002">
              <Undo2 className="w-3.5 h-3.5" data-id="element-11003" />
              Ongedaan
            </button>}
          <button onClick={handleClear} disabled={!canClear} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-slate-700 bg-white/80 border border-slate-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label={mode === 'draw' ? 'Hele kladblad wissen' : 'Typvak leegmaken'} data-id="element-11004">
            <Eraser className="w-3.5 h-3.5" data-id="element-11005" />
            Wissen
          </button>
        </div>
      </div>

      {/* Paper surface — same warm amber paper feel for both modes so the
                    tactile "this is scratch paper" association stays consistent. */}
      <div ref={containerRef} className="flex-1 relative bg-[#fdf6e3] overflow-hidden" style={{
      backgroundImage: 'repeating-linear-gradient(transparent, transparent 27px, rgba(146, 109, 39, 0.15) 27px, rgba(146, 109, 39, 0.15) 28px)'
    }} data-id="element-11006">
        {mode === 'draw' ? <>
            <canvas ref={canvasRef} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} onPointerLeave={handlePointerUp} className="absolute inset-0 w-full h-full touch-none cursor-crosshair" aria-label="Kladpapier tekenvlak" role="img" data-id="element-11007" />
            {!hasInk && <div className="pointer-events-none absolute inset-0 flex items-center justify-center" data-id="element-11008">
                <p className="text-amber-700/40 text-sm font-medium italic" data-id="element-11009">
                  Schrijf of teken hier je tussenstappen…
                </p>
              </div>}
          </> : <textarea ref={textareaRef} value={typedText} onChange={e => setTypedText(e.target.value)} placeholder="Typ hier je tussenstappen…
Bijvoorbeeld:
  12 × 25 = ?
  12 × 25 = 12 × 100 ÷ 4
        = 1200 ÷ 4
        = 300" spellCheck={false} className="absolute inset-0 w-full h-full resize-none bg-transparent outline-none p-4 text-slate-800 text-base leading-7 placeholder:text-amber-700/40 placeholder:italic" style={{
        fontFamily: '"JetBrains Mono", "Fira Code", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        lineHeight: '28px'
      }} aria-label="Kladpapier typvlak" data-id="element-11010" />}
      </div>
    </div>;
}